import React, { useEffect, type ReactElement } from 'react';
import { styled as materialStyled } from '@mui/material/styles';
import { useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { Link } from 'react-router-dom';
import { useFormik } from 'formik';
import * as yup from 'yup';
import * as _ from 'lodash';
import { CommonButtonV1, CommonErrorHelperTextV1, CommonTextFieldV1 } from 'gemlux-common-components';
import { authActions } from '../../redux/auth/authSlice';
import GreenCheckCircleSVG from '../../assets/img/green-check-frame.svg';
import { RootState } from '../../redux/store';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { validation } from '../../schema/validation';
import { LOGIN_ROUTE } from '../../utils/routes';
import LogoIcon from '../../assets/img/logo-large.svg';
import AlertIcon from '../../assets/img/alert.svg';
import CustomContainerBox from '../../components/V1/CustomContainerBox/CustomContainerBox';

const CustomBox = materialStyled(Box)(() => ({
  padding: '32px 0px 56px 0px',
}));

export default function ForgotPassword(): ReactElement {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const componentStatus = useAppSelector((state: RootState) => state.auth.componentStatus);
  const isEmailSent = _.get(componentStatus, 'isEmailSent', false) as boolean;
  const error = _.get(componentStatus, 'error', '') as string;
  const isSubmitting = _.get(componentStatus, 'isSubmitting', false) as boolean;

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: yup.object({
      email: validation.email,
    }),
    onSubmit: ({ email }) => {
      dispatch(authActions.setComponentStatus({ isSubmitting: true, isEmailSent: false, error: '' }));
      dispatch(authActions.sendPasswordResetLinkAsync({ email }));
    },
  });

  const resetErrorState = () => {
    dispatch(authActions.setComponentStatus({ error: '' }));
  };

  const enterKeyDownHandler = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();

      formik.handleSubmit();
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', enterKeyDownHandler);
    return () => {
      document.removeEventListener('keydown', enterKeyDownHandler);
    };
  }, []);

  if (!isEmailSent) {
    return (
      <CustomContainerBox>
        <CustomBox display="flex" justifyContent="center">
          <Box component="img" src={LogoIcon} alt="logo-icon" />
        </CustomBox>
        <Box display="flex" flexDirection="column" justifyContent="center" gap="10px">
          <Typography variant="textH3" textAlign="center">
            Forgot Password
          </Typography>

          <Typography variant="textBase" textAlign="center" color="#525E6F">
            A Reset Password link will be sent to you by email.
          </Typography>
        </Box>

        <Box display="flex" flexDirection="column" justifyContent="space-around" alignItems="center" width="100%">
          <Box display="flex" flexDirection="column" width="100%" gap="6px">
            <Typography variant="textBase500">Email Address</Typography>
            <CommonTextFieldV1
              type="email"
              name="email"
              placeholder="Email Address"
              fullWidth
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={
                formik.touched.email &&
                Boolean(formik.errors.email) && <CommonErrorHelperTextV1 errorText={formik.errors.email ?? ''} />
              }
              FormHelperTextProps={{ style: { ...theme.typography.meta, marginLeft: 0 } }}
              variant="outlined"
            />
          </Box>
        </Box>
        <Box display="flex" flexDirection="column" width="100%" gap={3}>
          <CommonButtonV1
            loading={isSubmitting}
            variant="contained"
            color="primary"
            fullWidth
            onClick={() => formik.handleSubmit()}
          >
            Send Reset Link
          </CommonButtonV1>

          <Box display="flex" justifyContent="center">
            <Link style={{ textDecoration: 'none' }} onClick={resetErrorState} to={LOGIN_ROUTE}>
              <Typography variant="subheadRegular" color={theme.palette.shades.primaryBrightDark}>
                Remember Password?
              </Typography>
            </Link>
          </Box>
        </Box>
        {error && (
          <Box display="flex" gap={1}>
            <Box src={AlertIcon} component="img" />
            <Typography variant="footnoteRegular2" component="p" textAlign="center" color="#CB0B38">
              {error}
            </Typography>
          </Box>
        )}
      </CustomContainerBox>
    );
  }
  return (
    <CustomContainerBox>
      <Box component="img" display="flex" margin="auto" src={LogoIcon} alt="green-check-circle-icon" />
      <Box
        component="img"
        display="flex"
        margin="auto"
        src={GreenCheckCircleSVG}
        alt="green-check-circle-icon"
        mt="56px"
      />

      <Box display="flex" flexDirection="column" gap={2}>
        <Typography variant="textH5" textAlign="center">
          Password Reset Link Sent!
        </Typography>
        <Typography variant="textBase" textAlign="center" color="text.secondary">
          We have sent a password reset link to your email address if you have entered a valid email address. Please
          check your email and follow the instructions to reset your password.
        </Typography>
      </Box>
    </CustomContainerBox>
  );
}
